import React, { useEffect, useState } from "react";
import { Modal, Form, Input, List, Progress, Tag, message, Alert } from "antd";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import SelectOrg from "../SelectOrg/SelectOrg";
import { copyDocToOrg } from "./api";

// Copia in blocco dei documenti selezionati verso un'org destinazione.
// Props:
//   visible, onCancel
//   selectedDocs: [{ objectId, nome_documento }]
//   onDone(): callback dopo una copia completata (per chiudere/azzerare la selezione)
const CopyToOrgModal = ({ visible, onCancel, selectedDocs, onDone }) => {
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const [targetOrgId, setTargetOrgId] = useState(undefined);
  const [names, setNames] = useState({});
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [results, setResults] = useState([]); // [{ id, nome, ok }]

  // Inizializza i nomi editabili dai documenti selezionati a ogni apertura.
  useEffect(() => {
    if (visible) {
      const init = {};
      selectedDocs.forEach((d) => { init[d.objectId] = d.nome_documento || ""; });
      setNames(init);
      setResults([]);
      setDone(0);
      setTargetOrgId(undefined);
    }
  }, [visible, selectedDocs]);

  const handleCopy = async () => {
    if (!targetOrgId) {
      message.warning("Seleziona l'org di destinazione");
      return;
    }
    setRunning(true);
    setResults([]);
    setDone(0);
    const token = await getTokenSilently();
    const acc = [];
    for (let i = 0; i < selectedDocs.length; i += 1) {
      const d = selectedDocs[i];
      const nome = names[d.objectId] || d.nome_documento || "";
      try {
        await copyDocToOrg(token, env, d.objectId, targetOrgId, nome);
        acc.push({ id: d.objectId, nome, ok: true });
      } catch (e) {
        console.error("Errore copia documento:", d.objectId, e);
        acc.push({ id: d.objectId, nome, ok: false });
      }
      setDone(i + 1);
      setResults([...acc]);
    }
    setRunning(false);
    const okCount = acc.filter((r) => r.ok).length;
    if (okCount === 0) {
      message.error(`Nessun documento copiato (0/${selectedDocs.length})`);
    } else {
      message.success(`Copiati ${okCount}/${selectedDocs.length} documenti`);
      onDone();
    }
  };

  const resultById = (id) => results.find((r) => r.id === id);
  const pct = selectedDocs.length ? Math.round((done / selectedDocs.length) * 100) : 0;

  return (
    <Modal
      title={`Copia ${selectedDocs.length} documenti verso un'org`}
      open={visible}
      onOk={handleCopy}
      onCancel={onCancel}
      okText="Copia"
      cancelText="Chiudi"
      confirmLoading={running}
      width={640}
      maskClosable={false}
    >
      <Form layout="vertical">
        <Form.Item label="Org di destinazione" required>
          <SelectOrg value={targetOrgId} onChange={setTargetOrgId} />
        </Form.Item>
      </Form>

      {running || results.length > 0 ? (
        <Progress percent={pct} status={running ? "active" : "normal"} />
      ) : (
        <Alert
          type="info"
          showIcon
          message="Puoi modificare il nome di ciascuna copia prima di confermare."
          style={{ marginBottom: 12 }}
        />
      )}

      <List
        size="small"
        dataSource={selectedDocs}
        style={{ maxHeight: 320, overflow: "auto" }}
        renderItem={(d) => {
          const r = resultById(d.objectId);
          return (
            <List.Item
              extra={
                r ? (
                  <Tag color={r.ok ? "green" : "red"}>{r.ok ? "Creato" : "Errore"}</Tag>
                ) : null
              }
            >
              <Input
                value={names[d.objectId]}
                disabled={running}
                onChange={(e) =>
                  setNames((n) => ({ ...n, [d.objectId]: e.target.value }))
                }
              />
            </List.Item>
          );
        }}
      />
    </Modal>
  );
};

export default CopyToOrgModal;
